import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, Pencil, Plus, X } from 'lucide-react'
import { useId, useState } from 'react'
import {
  type AdminStore,
  type CreateAdminStoreInput,
  createAdminStore,
  listAdminStores,
  type UpdateAdminStoreInput,
  updateAdminStore,
} from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { describeErrorMessage } from '@/lib/errors'

export const Route = createFileRoute('/admin/stores')({
  component: AdminStoresPage,
})

function AdminStoresPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [editStore, setEditStore] = useState<AdminStore | null>(null)

  const query = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: listAdminStores,
  })
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: createAdminStore,
    onSuccess: () => {
      setCreateOpen(false)
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (payload: { id: string; input: UpdateAdminStoreInput }) =>
      updateAdminStore(payload.id, payload.input),
    onSuccess: () => {
      setEditStore(null)
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
    },
  })

  return (
    <main className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        to="/admin"
        className="-ml-3 inline-flex w-fit items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />В админ-панель
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Магазины</h1>
          <p className="text-sm text-muted-foreground">
            {query.data ? `Всего: ${query.data.length}` : 'Загружаем список…'}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Добавить
        </Button>
      </header>

      {query.isLoading ? (
        <StatusCard title="Загружаем магазины" />
      ) : query.isError ? (
        <StatusCard
          title="Не удалось загрузить"
          description={describeErrorMessage(query.error, 'Попробуйте обновить.')}
        />
      ) : query.data && query.data.length === 0 ? (
        <StatusCard
          title="Магазинов пока нет"
          description="Добавьте первый магазин, чтобы привязывать продавцов."
        />
      ) : (
        <StoresTable stores={query.data ?? []} onEdit={setEditStore} />
      )}

      {createOpen && (
        <StoreFormDialog
          title="Новый магазин"
          submitLabel="Создать"
          initial={{ name: '', city: '' }}
          onClose={() => setCreateOpen(false)}
          onSubmit={(input) => createMutation.mutate(input)}
          pending={createMutation.isPending}
          error={createMutation.error}
        />
      )}
      {editStore && (
        <StoreFormDialog
          title={`Редактировать: ${editStore.name}`}
          submitLabel="Сохранить"
          initial={{ name: editStore.name, city: editStore.city }}
          onClose={() => setEditStore(null)}
          onSubmit={(input) =>
            updateMutation.mutate({
              id: editStore.id,
              input: {
                name: input.name === editStore.name ? undefined : input.name,
                city: input.city === editStore.city ? undefined : input.city,
              },
            })
          }
          pending={updateMutation.isPending}
          error={updateMutation.error}
        />
      )}
    </main>
  )
}

interface StoresTableProps {
  stores: AdminStore[]
  onEdit: (s: AdminStore) => void
}

function StoresTable({ stores, onEdit }: StoresTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            <th className="px-4 py-3 font-medium">Название</th>
            <th className="px-4 py-3 font-medium">Город</th>
            <th className="px-4 py-3 font-medium">Статус</th>
            <th className="px-4 py-3 text-right font-medium">Действия</th>
          </tr>
        </thead>
        <tbody>
          {stores.map((store) => (
            <tr key={store.id} className="border-t">
              <td className="px-4 py-3 font-medium">{store.name}</td>
              <td className="px-4 py-3 text-muted-foreground">{store.city}</td>
              <td className="px-4 py-3">{store.status === 'active' ? 'Активен' : store.status}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => onEdit(store)}>
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                    Изменить
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface StoreFormDialogProps {
  title: string
  submitLabel: string
  initial: CreateAdminStoreInput
  onClose: () => void
  onSubmit: (input: CreateAdminStoreInput) => void
  pending: boolean
  error: unknown
}

function StoreFormDialog({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  pending,
  error,
}: StoreFormDialogProps) {
  const [name, setName] = useState(initial.name)
  const [city, setCity] = useState(initial.city)
  const nameId = useId()
  const cityId = useId()

  return (
    <Modal title={title} onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit({ name, city })
        }}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor={nameId}>Название</Label>
          <Input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={1}
            maxLength={200}
            placeholder="Citilux Тверская"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={cityId}>Город</Label>
          <Input
            id={cityId}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            minLength={1}
            maxLength={100}
            placeholder="Москва"
          />
        </div>
        <ErrorBanner error={error} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Сохраняем…' : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

interface ModalProps {
  title: string
  onClose: () => void
  children: React.ReactNode
}

function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="flex-row items-start justify-between">
          <CardTitle>{title}</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="-mr-2 -mt-2 h-7 w-7"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">{children}</CardContent>
      </Card>
    </div>
  )
}

function ErrorBanner({ error }: { error: unknown }) {
  if (!error) {
    return null
  }
  const message = describeErrorMessage(error, 'Не удалось выполнить действие.')
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

function StatusCard({ title, description }: { title: string; description?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
    </Card>
  )
}
